import React, { useState, useRef, useEffect } from 'react';

const EnhancedDateTimePicker = ({ 
  value, 
  onChange, 
  label, 
  required = false, 
  minDate = null, 
  isAllDay = false,
  className = "",
  placeholder = "Select date and time"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const pickerRef = useRef(null);

  // Initialize from value prop
  useEffect(() => {
    if (value) {
      const date = new Date(value);
      setSelectedDate(date);
      if (!isAllDay) {
        setSelectedTime(date.toTimeString().slice(0, 5));
      }
    }
  }, [value, isAllDay]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const handleDateSelect = (day) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    setSelectedDate(newDate);
    
    // Update the value with time if available
    if (selectedTime && !isAllDay) {
      const [hours, minutes] = selectedTime.split(':');
      newDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    }
    
    onChange(newDate.toISOString().slice(0, 16));
  };

  const handleTimeChange = (time) => {
    setSelectedTime(time);
    if (selectedDate) {
      const [hours, minutes] = time.split(':');
      const newDate = new Date(selectedDate);
      newDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      onChange(newDate.toISOString().slice(0, 16));
    }
  };

  const formatDisplayDate = () => {
    if (!value) return placeholder;
    
    const date = new Date(value);
    const dateStr = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    
    if (isAllDay) {
      return dateStr;
    }
    
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    
    return `${dateStr} at ${timeStr}`;
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const isSelected = (date) => {
    if (!selectedDate) return false;
    return date.getDate() === selectedDate.getDate() &&
           date.getMonth() === selectedDate.getMonth() &&
           date.getFullYear() === selectedDate.getFullYear();
  };

  const isDisabled = (date) => {
    if (minDate) {
      const min = new Date(minDate);
      return date < min;
    }
    return false;
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(month, year);
    const firstDay = getFirstDayOfMonth(month, year);
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      days.push(date);
    }
    
    return days;
  };

  const timeOptions = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      timeOptions.push(time);
    }
  }

  return (
    <div className={`relative ${className}`}>
      <label className="block text-sm font-medium text-klorah-text tracking-wider mb-2">
        {label} {required && '*'}
      </label>
      
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full p-3 bg-klorah-card border border-klorah-border rounded-xl text-left text-white focus:outline-none focus:ring-2 focus:ring-klorah-primary transition-all hover:border-klorah-primary/50 ${
          !value ? 'text-klorah-text-light' : ''
        }`}
      >
        <div className="flex items-center justify-between">
          <span>{formatDisplayDate()}</span>
          <svg className="w-4 h-4 text-klorah-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {isOpen && (
        <div 
          ref={pickerRef}
          className="absolute top-full left-0 right-0 mt-2 bg-klorah-card border border-klorah-border rounded-xl shadow-2xl z-50 p-4 min-w-[320px]"
        >
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={() => navigateMonth(-1)}
              className="p-2 text-klorah-text hover:text-white hover:bg-klorah-dark rounded-lg transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <h3 className="text-white font-medium">
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h3>
            
            <button
              type="button"
              onClick={() => navigateMonth(1)}
              className="p-2 text-klorah-text hover:text-white hover:bg-klorah-dark rounded-lg transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="mb-4">
            {/* Day Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-xs text-klorah-text-light text-center py-1">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-1">
              {generateCalendarDays().map((date, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => date && !isDisabled(date) && handleDateSelect(date.getDate())}
                  disabled={!date || isDisabled(date)}
                  className={`w-8 h-8 text-sm rounded-lg transition-all ${
                    !date 
                      ? 'invisible' 
                      : isDisabled(date)
                      ? 'text-klorah-text-light cursor-not-allowed'
                      : isSelected(date)
                      ? 'bg-klorah-primary text-white'
                      : isToday(date)
                      ? 'bg-klorah-primary/20 text-klorah-primary hover:bg-klorah-primary/30'
                      : 'text-white hover:bg-klorah-dark'
                  }`}
                >
                  {date?.getDate()}
                </button>
              ))}
            </div>
          </div>

          {/* Time Picker - Only show if not all day */}
          {!isAllDay && (
            <div className="border-t border-klorah-border pt-4">
              <label className="block text-sm font-medium text-klorah-text mb-2">
                Time
              </label>
              <select
                value={selectedTime}
                onChange={(e) => handleTimeChange(e.target.value)}
                className="w-full p-2 bg-klorah-dark border border-klorah-border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-klorah-primary"
              >
                <option value="">Select time</option>
                {timeOptions.map(time => (
                  <option key={time} value={time}>
                    {new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Quick Actions */}
          <div className="flex space-x-2 mt-4 pt-4 border-t border-klorah-border">
            <button
              type="button"
              onClick={() => {
                const now = new Date();
                setSelectedDate(now);
                if (!isAllDay) {
                  setSelectedTime(now.toTimeString().slice(0, 5));
                }
                onChange(now.toISOString().slice(0, 16));
                setIsOpen(false);
              }}
              className="flex-1 px-3 py-2 text-xs bg-klorah-primary/20 text-klorah-primary rounded-lg hover:bg-klorah-primary/30 transition-all"
            >
              Now
            </button>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="flex-1 px-3 py-2 text-xs bg-klorah-dark text-klorah-text rounded-lg hover:bg-klorah-border transition-all"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedDateTimePicker; 